import { useBaseInfoStore } from '@/pages/clusters/pages/Workload/create/base-info';
import { WorkloadParams } from '@/pages/clusters/pages/Workload/types';
import { PATTERN_ENV_NAME } from '@/utils';
import { Button, Form, FormInstance, Grid, Input, Select } from '@arco-design/web-react';
import { IconDelete } from '@arco-design/web-react/icon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useConfigKeyMaps, useSecretKeymaps } from '../hooks';
import { EnvItem } from './_hooks';
import { EnvFormData } from './environment-variable';

const Row = Grid.Row;
const Col = Grid.Col;

interface EnvironmentVariableItemProps {
  field: string;
  index: number;
  remove: (index: number) => void;
  form: FormInstance<EnvFormData>;
  data: EnvItem[];
}

export function EnvironmentVariableItem({
  field,
  index,
  remove,
  form,
  data,
}: EnvironmentVariableItemProps) {
  const { t } = useTranslation();
  const msg = {
    key: t('common:key'),
    value: t('common:value'),
    resource: t('components:resource'),
    key_in_resource: t('components:key.in.resource'),
    from_custom: t('components:custom'),
    from_config_map: t('components:from.configmap'),
    from_secret: t('components:from.secret'),
    invalid_key: t(
      'components:environment.variable.keys.can.only.contain.letters.numbers.underscores.hyphens.and.periods.and.cannot.start.with.a.number',
    ),
    empty_key: t('components:please.set.the.environment.variable.key'),
  };

  const { cluster } = useParams<WorkloadParams>();

  const initType = data?.[index]?.type || 'custom';
  const [type, setType] = useState<EnvItem['type']>(initType);
  const { namespace } = useBaseInfoStore((state) => state.baseInfo);

  const { options: configsOptions, isLoading: isLoadingConfigs } = useConfigKeyMaps(
    cluster,
    namespace,
  );
  const { options: secretsOptions, isLoading: isLoadingSecrets } = useSecretKeymaps(
    cluster,
    namespace,
  );
  // 当前选中的资源及其对应的键选项
  const [configKeys, setConfigKeys] = useState<{ label: string; value: string }[]>([]);
  const [secretKeys, setSecretKeys] = useState<{ label: string; value: string }[]>([]);

  // 选择环境变量的类型
  const handleTypeChange = (typeValue: EnvItem['type']) => {
    setType(typeValue);
    // 无论切换到哪种类型，都先重置资源和 value 字段, 在 UI 表单收集数据的时候,这两者互斥.
    form.setFieldsValue({
      [`${field}.type`]: typeValue,
      [`${field}.resource`]: undefined,
      [`${field}.resourceKey`]: undefined,
      [`${field}.value`]: undefined,
    });

    // 根据类型设置不同的字段
    switch (typeValue) {
      case 'configMapKeyRef':
        // 清除 secretKeys 状态
        setSecretKeys([]);
        break;
      case 'secretKeyRef':
        // 清除 configKeys 状态
        setConfigKeys([]);
        break;
      case 'custom':
      default:
        // 清除两种引用类型的状态
        setConfigKeys([]);
        setSecretKeys([]);
        break;
    }
  };

  // 处理配置字典选择变化
  const handleConfigMapChange = (value: string) => {
    // 重置键值选择
    form.setFieldsValue({
      [`${field}.resourceKey`]: undefined,
    });
    // 查找当前选中的配置字典
    const selectedConfig = configsOptions.find((option) => option.value === value);
    // 更新键选项
    if (selectedConfig && selectedConfig.extraOptions) {
      setConfigKeys(selectedConfig.extraOptions);
    } else {
      setConfigKeys([]);
    }
  };

  // 处理保密字典选择变化
  const handleSecretChange = (value: string) => {
    // 重置键值选择
    form.setFieldsValue({
      [`${field}.resourceKey`]: undefined,
    });
    // 查找当前选中的保密字典
    const selectedSecret = secretsOptions.find((option) => option.value === value);
    // 更新键选项
    if (selectedSecret && selectedSecret.extraOptions) {
      setSecretKeys(selectedSecret.extraOptions);
    } else {
      setSecretKeys([]);
    }
  };

  // 根据类型渲染不同的字段
  const renderValueFrom = () => {
    switch (type) {
      /** 保密字典 */
      case 'secretKeyRef':
        return (
          <>
            <Col span={7}>
              <Form.Item
                field={`${field}.key`}
                rules={[
                  { required: true, message: msg.empty_key },
                  { match: PATTERN_ENV_NAME, message: msg.invalid_key },
                ]}
              >
                <Input placeholder={msg.key} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item field={`${field}.resource`}>
                <Select
                  showSearch
                  placeholder={msg.resource}
                  options={secretsOptions}
                  onChange={handleSecretChange}
                  loading={isLoadingSecrets}
                />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item field={`${field}.resourceKey`}>
                <Select
                  showSearch
                  placeholder={msg.key_in_resource}
                  options={secretKeys}
                  loading={isLoadingSecrets}
                />
              </Form.Item>
            </Col>
          </>
        );
      /** 配置字典 */
      case 'configMapKeyRef':
        return (
          <>
            <Col span={7}>
              <Form.Item
                field={`${field}.key`}
                rules={[
                  { required: true, message: msg.empty_key },
                  { match: PATTERN_ENV_NAME, message: msg.invalid_key },
                ]}
              >
                <Input placeholder={msg.key} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item field={`${field}.resource`}>
                <Select
                  showSearch
                  placeholder={msg.resource}
                  options={configsOptions}
                  onChange={handleConfigMapChange}
                  loading={isLoadingConfigs}
                />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item field={`${field}.resourceKey`}>
                <Select
                  showSearch
                  placeholder={msg.key_in_resource}
                  options={configKeys}
                  loading={isLoadingConfigs}
                />
              </Form.Item>
            </Col>
          </>
        );
      /** 自定义 */
      case 'custom':
      default:
        return (
          <>
            <Col span={10}>
              <Form.Item
                field={`${field}.key`}
                rules={[
                  { required: true, message: msg.empty_key },
                  { match: PATTERN_ENV_NAME, message: msg.invalid_key },
                ]}
              >
                <Input placeholder={msg.key} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item field={`${field}.value`}>
                <Input placeholder={msg.value} />
              </Form.Item>
            </Col>
          </>
        );
    }
  };

  const typeOptions = [
    { label: msg.from_custom, value: 'custom' },
    { label: msg.from_config_map, value: 'configMapKeyRef' },
    { label: msg.from_secret, value: 'secretKeyRef' },
  ];

  return (
    <div key={index} style={{ display: 'flex' }}>
      <Row gutter={8} style={{ flex: 1 }}>
        <Col span={4}>
          <Form.Item>
            <Select
              showSearch
              options={typeOptions}
              defaultValue="custom"
              onChange={handleTypeChange}
              value={type}
            />
          </Form.Item>
        </Col>
        {renderValueFrom()}
      </Row>
      <Button
        icon={<IconDelete />}
        type="text"
        onClick={() => remove(index)}
        style={{ marginLeft: 8 }}
      />
    </div>
  );
}
